import React, { useEffect, useState, Suspense } from 'react';

import _ from 'lodash';
import cx from 'classnames';

import { DefaultAssetViewer } from './defaultViewer';
import { getContentForFieldSelector } from './fields';
import Content from '../widgets/core/content';

import { WidgetsContext } from "./../widgets/core";

import { DocumentContentViewer } from './documentViewer';

import styles from './assetviewer.module.scss';

const HTMLViewer = React.lazy(
  () => import('@paperflite-marketing/asset-viewer').then(module => ({ default: module.HTMLViewer }))
);

const ImageViewer = React.lazy(
  () => import('@paperflite-marketing/asset-viewer').then(module => ({ default: module.ImageViewer }))
);

const VideoViewer = React.lazy(
  () => import('@paperflite-marketing/asset-viewer').then(module => ({ default: module.VideoViewer }))
);

const AudioViewer = React.lazy(
  () => import('@paperflite-marketing/asset-viewer').then(module => ({ default: module.AudioViewer }))
);

export const AssetContentViewer = ({ asset, active, onLoad, loaded, onAnalyticsChange, onDebugAnalytics, scrollParent, inline, widgetId, poster }) => {
  const id = _.get(asset, 'content.asset.id');
  const type = _.get(asset, 'content.asset.viewer.type');
  const [width, setWidth] = useState(1200);
  const [render, setRender] = React.useState(!inline);

  const context = React.useContext(WidgetsContext);

  React.useEffect(() => {
    window.addEventListener('resize', handleWidowResize);
    calculatePreferredWidth();
    handleViewerLoaded();
    return () => {
      window.removeEventListener('resize', handleWidowResize);
    };
  }, []);

  React.useEffect(() => {
    if (!render) {
      setRender(true);
    }
  }, [])

  const handleWidowResize = () => {
    calculatePreferredWidth();
  }

  const calculatePreferredWidth = () => {
    const viewerContainer = document.querySelector(`[data-asset="${_.get(asset, 'uid', null)}"]`);
    if (viewerContainer) {
      const width = viewerContainer.offsetWidth;
      setWidth(width);
    }
  }

  const handleViewerLoaded = () => {
    if (!_.includes(['enhanced_viewer', 'pdf_viewer'], type)) {
      onLoad();
    }
  }

  if (!render) {
    return null;
  }

  switch (type) {
    case 'enhanced_viewer':
    case 'pdf_viewer': {
      return (
        <DocumentContentViewer
          asset={asset}
          width={width}
          active={active}
          widgetId={widgetId}
          inline={inline}
          onLoad={onLoad}
          loaded={loaded}
          scrollParent={scrollParent}
          onAnalyticsChange={onAnalyticsChange}
          onDebugAnalytics={onDebugAnalytics}
        />
      );
    }

    case 'html_viewer': {
      const id = _.get(asset, 'content.asset.id');
      const mode = _.get(asset, 'content.asset.viewer.mode');
      const url = _.get(asset, 'content.asset.metadata.url');
      const optimizedUrl = _.get(asset, 'content.asset.viewer.url');
      const canPreview = _.get(asset, 'content.asset.viewer.preview');

      const name = getContentForFieldSelector(asset, 'name') || "";
      const icon = getContentForFieldSelector(asset, 'icon');
      const iconUrl = _.get(icon, 'url') || '/public/cleverstory/v2/static/default_card.jpg';

      return (
        <Suspense fallback={<div className={styles.htmlViewer}></div>}>
          <HTMLViewer
            id={widgetId ? widgetId : id}
            className={styles.htmlViewer}
            name={name}
            icon={iconUrl}
            mode={mode}
            url={url}
            optimizedUrl={optimizedUrl}
            canPreview={canPreview}
            active={active}
            onAnalyticsChange={onAnalyticsChange}
            onDebugAnalytics={onDebugAnalytics}
          />
        </Suspense>
      );
    }

    case 'image_viewer': {
      const id = _.get(asset, 'content.asset.id');
      const sourceUrl = _.get(asset, 'content.asset.viewer.url');

      return (
        <Suspense fallback={<div className={styles.imageViewer}></div>}>
          <ImageViewer
            id={`asset_viewer_${widgetId ? widgetId : id}`}
            className={styles.imageViewer}
            src={sourceUrl}
            scrollParent={`.${styles.assetViewerContent}`}
            loadingComponent={() => {
              return (
                <div className={cx(styles.loader)}>
                  <div className={styles.videoWrapper}>
                    <video loop autoPlay playsInline muted>
                      <source src="/images/Workspaceloader.webm" type="video/webm" />
                      <source src="/images/Workspaceloader.mp4" type="video/mp4" />
                    </video>
                    <label className={styles.message}>Loading Image....</label>
                  </div>
                </div>
              );
            }}
            active={active}
            onAnalyticsChange={onAnalyticsChange}
            onDebugAnalytics={onDebugAnalytics}
          />
        </Suspense>
      );
    }

    case 'video_viewer': {
      const id = _.get(asset, 'content.asset.id');
      const metadata = _.get(asset, 'content.asset.metadata');
      const sourceUrl = _.get(asset, 'content.asset.viewer.url');
      const videoProvider = _.get(metadata, 'external_video_provider');

      const icon = getContentForFieldSelector(asset, 'icon');
      const iconUrl = _.get(icon, 'url') || '/public/cleverstory/v2/static/default_card.jpg';

      if (videoProvider == null || videoProvider == 'youtube' || videoProvider == 'vimeo' || videoProvider == 'wistia') {
        return (
          <Suspense fallback={<div className={cx(styles.videoViewer, { [styles.inline]: inline })}></div>}>
            <VideoViewer
              id={`asset_viewer_${widgetId ? widgetId : id}`}
              className={cx(styles.videoViewer, { [styles.inline]: inline })}
              src={sourceUrl}
              metadata={metadata}
              active={active}
              icon={iconUrl}
              imageCDN={context.imageCDN}
              poster={poster}
              onPlay={() => {
                // console.log("Video started playing");
              }}
              onAnalyticsChange={onAnalyticsChange}
              onDebugAnalytics={onDebugAnalytics}
            />
          </Suspense>
        );
      }
    }

    case 'audio_viewer': {
      const id = _.get(asset, 'content.asset.id');
      const metadata = _.get(asset, 'content.asset.metadata');
      const sourceUrl = _.get(asset, 'content.asset.viewer.url');
      const supportedFormats = ['mp3', 'wav', 'aiff', 'flac', 'wma', 'aac'];
      const icon = getContentForFieldSelector(asset, 'icon');
      const iconUrl = _.get(icon, 'url') || '/public/cleverstory/v2/static/default_card.jpg';
      return (
        <Suspense fallback={<div className={styles.defaultViewer}></div>}>
          { _.includes(supportedFormats, _.get(metadata, 'extension')) &&
            <AudioViewer
              id={`asset_viewer_${widgetId ? widgetId : id}`}
              className={cx(styles.videoViewer, { [styles.inline]: inline })}
              src={sourceUrl}
              metadata={metadata}
              active={active}
              icon={iconUrl}
              imageCDN={context.imageCDN}
              poster={poster}
              onPlay={() => {
                // console.log("Video started playing");
              }}
              onAnalyticsChange={onAnalyticsChange}
              onDebugAnalytics={onDebugAnalytics}
            />
          }
          {!_.includes(supportedFormats, _.get(metadata, 'extension')) &&
            <DefaultAssetViewer
              asset={asset}
              active={active}
              onAnalyticsChange={onAnalyticsChange}
              onDebugAnalytics={onDebugAnalytics}
            />
          }
        </Suspense>
      )
    }

    default:
      return (
        <Suspense fallback={<div className={styles.defaultViewer}></div>}>
          <DefaultAssetViewer
            asset={asset}
            active={active}
            onAnalyticsChange={onAnalyticsChange}
            onDebugAnalytics={onDebugAnalytics}
          />
        </Suspense>
      );
  }
}
